import { useEffect, useState, type FC } from 'react';
import type { PreviewRendererProps } from '../types';
import { ErrorIndicator, LoadingIndicator } from '../components';

export const ChildComponentRenderer = ({ loader }: PreviewRendererProps<'childComponent' | 'preview3d'>) => {
    const [childComponent, setChildComponent] = useState<FC | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const loadChildComponent = async () => {
            try {
                setIsLoading(true);
                setHasError(false);
                setErrorMessage('');

                const data = await loader({
                    onStatusUpdate: () => {},
                    signal: abortController.signal,
                });

                if (isMounted && data?.childComponent) {
                    setChildComponent(() => data.childComponent);
                }
            } catch (error) {
                if (isMounted) {
                    setHasError(true);
                    setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadChildComponent();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [loader]);

    if (isLoading) {
        return <LoadingIndicator label="Loading..." />;
    }

    if (hasError) {
        return <ErrorIndicator message={errorMessage} />;
    }

    if (childComponent) {
        const ChildComponent = childComponent;
        return <ChildComponent />;
    }

    return null;
};
